import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { HiPencil, HiChevronDown } from "react-icons/hi";
import { useSnackbar } from "notistack";
import {
  useAdminUpdateRole,
  useAdminPrivileges,
  useAdminUpdateRoleTiers,
  useAdminRolePrivileges,
} from "@/api/hooks/admin.hooks";
import RoleTierEditor from "./RoleTierEditor";
import type { RoleTierEditorHandle } from "./RoleTierEditor";
import type { RoleDto } from "@/api/types/dtos/role-privilege.dto";

interface EditRoleModalProps {
  open: boolean;
  role: RoleDto | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditRoleModal: React.FC<EditRoleModalProps> = ({ open, role, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const updateRoleMutation = useAdminUpdateRole();
  const updateTiersMutation = useAdminUpdateRoleTiers();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const tierEditorRef = useRef<RoleTierEditorHandle>(null);

  const { data: privilegesData } = useAdminPrivileges();
  const allPrivileges = (privilegesData?.data ?? []).map((p) => ({
    id: p.id,
    name: p.name,
  }));

  const privilegeIdToName = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of allPrivileges) {
      map.set(p.id, p.name);
    }
    return map;
  }, [allPrivileges]);

  const { data: rolePrivilegesData } = useAdminRolePrivileges(role?.id || "");
  const existingPrivilegeNames = (rolePrivilegesData?.data ?? [])
    .filter((rp) => rp.isActive)
    .map((rp) => privilegeIdToName.get(rp.privilegeId))
    .filter(Boolean) as string[];

  const privilegesLoaded = !!rolePrivilegesData?.data;

  useEffect(() => {
    if (role) {
      setName(role.name || "");
      setDescription(role.description || "");
      setIsActive(role.isActive);
      setErrors({});
    }
  }, [role]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) {
      newErrors.name = "El nombre del rol es obligatorio";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!role || !validate()) return;

    try {
      await updateRoleMutation.mutateAsync({
        id: role.id,
        data: {
          id: role.id,
          name: name.trim(),
          description: description.trim() || undefined,
          organizationId: role.organizationId,
          isActive,
        },
      } as any);

      const tierChanges = tierEditorRef.current?.getTierChanges() ?? [];
      if (tierChanges.length > 0) {
        await updateTiersMutation.mutateAsync({
          roleId: role.id,
          tiers: tierChanges,
        });
      }

      enqueueSnackbar("Rol actualizado exitosamente", { variant: "success" });
      onSuccess();
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { description?: string; message?: string } };
      };
      const message =
        axiosError?.response?.data?.description ||
        axiosError?.response?.data?.message ||
        "Error al actualizar el rol";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleClose = () => {
    if (updateRoleMutation.isPending || updateTiersMutation.isPending) return;
    setErrors({});
    onClose();
  };

  const isPending = updateRoleMutation.isPending || updateTiersMutation.isPending;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="edit-role-dialog-title"
      PaperProps={{
        sx: { borderTop: "4px solid", borderColor: "primary.main" },
      }}
    >
      <DialogTitle id="edit-role-dialog-title" sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <HiPencil size={22} />
          <Box>
            <Typography variant="h6" component="span" fontWeight={600}>
              Editar Rol
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {role?.name || ""}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Información General
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <TextField
                  label="Nombre"
                  required
                  fullWidth
                  size="small"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  disabled={isPending}
                  id="edit-role-name"
                  inputProps={{
                    "aria-invalid": !!errors.name,
                    "aria-describedby": errors.name ? "edit-role-name-error" : undefined,
                  }}
                  FormHelperTextProps={{ id: "edit-role-name-error" }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      disabled={isPending}
                      color="success"
                    />
                  }
                  label={
                    <Chip
                      label={isActive ? "Activo" : "Inactivo"}
                      color={isActive ? "success" : "default"}
                      size="small"
                      variant="outlined"
                    />
                  }
                  sx={{ mr: 0, flexShrink: 0 }}
                />
              </Box>
              <TextField
                label="Descripción"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isPending}
              />
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Privilegios
            </Typography>
            <Accordion
              elevation={0}
              sx={{ border: "1px solid", borderColor: "divider" }}
              defaultExpanded
            >
              <AccordionSummary expandIcon={<HiChevronDown size={16} />}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Asignar Privilegios
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1 }}>
                {privilegesLoaded ? (
                  <RoleTierEditor
                    ref={tierEditorRef}
                    allPrivileges={allPrivileges}
                    initialPrivilegeNames={existingPrivilegeNames}
                  />
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={isPending} sx={{ minWidth: 100 }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isPending}
          startIcon={
            isPending ? <CircularProgress size={16} color="inherit" /> : <HiPencil size={16} />
          }
          sx={{ minWidth: 140 }}
        >
          {isPending ? "Guardando..." : "Guardar Cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRoleModal;
